// Procedural "bright Eno" chime that plays when an answer lands. No audio files:
// a soft, shimmering major-pentatonic cluster, with a small random transposition
// each time so it feels generative rather than a canned beep.

let actx: AudioContext | null = null;

function ctx(): AudioContext | null {
  if (actx) return actx;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    actx = new AC();
    return actx;
  } catch {
    return null;
  }
}

// Call from a user gesture (first send) so the AudioContext is allowed to run.
export function unlockSound(): void {
  const c = ctx();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem('neuro:muted') === '1';
  } catch {
    return false;
  }
}

export function setMuted(m: boolean): void {
  try {
    localStorage.setItem('neuro:muted', m ? '1' : '0');
  } catch {
    // ignore storage failures
  }
}

export function playLandChime(): void {
  if (isMuted()) return;
  const c = ctx();
  if (!c) return;
  try {
    if (c.state === 'suspended') c.resume().catch(() => {});
    const t0 = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.2;
    master.connect(c.destination);
    const lp = c.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 5400;
    lp.connect(master);

    const roots = [0, 2, 4, 7];
    const root = roots[Math.floor(Math.random() * roots.length)] ?? 0;
    const voicing = [0, 7, 12, 16]; // root, fifth, octave, +maj3 — bright and open

    voicing.forEach((s, k) => {
      const f = 523.25 * Math.pow(2, (root + s) / 12); // bright register, ~C5 base
      const g = c.createGain();
      g.connect(lp);
      const o = c.createOscillator();
      o.type = k > 1 ? 'sine' : 'triangle';
      o.frequency.value = f;
      const o2 = c.createOscillator(); // faint shimmer octave, slightly detuned
      o2.type = 'sine';
      o2.frequency.value = f * 2.001;
      const g2 = c.createGain();
      g2.gain.value = 0.16;
      o2.connect(g2);
      g2.connect(g);
      const st = t0 + k * 0.05;
      g.gain.setValueAtTime(0.0001, st);
      g.gain.exponentialRampToValueAtTime(0.9 / (k + 1), st + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, st + 1.6);
      o.start(st);
      o2.start(st);
      o.stop(st + 1.7);
      o2.stop(st + 1.7);
    });
  } catch {
    // audio is best-effort; never break the chat over a chime
  }
}
